import type { DsBrandDocumentGenerateStatusResponse } from '@/services/db/ds-brand-document/entity';
import type { DsGlobalStylesProcessStatusResponse } from '@/services/db/ds-global-styles/entity';
import type { BrandWizardDraft, BrandWizardDraftPhase } from '../wizard/draft-store';

/**
 * Shared brand-authoring-run derivation (CON-7724 resume follow-up). One pure
 * function turns the resumable draft (Task 1) plus the latest status poll
 * into a single view state, so the wizard, the Styles-page banner, and the
 * app-wide snackbar (later tasks) all agree on what a run is doing without
 * duplicating the terminal/stall rules three times.
 */

export const AUTHORING_POLL_INTERVAL_MS = 2500;
/** Matches the server's WorkflowExecutionTimeout for the authoring workflow. */
export const AUTHORING_STALL_MS = 25 * 60 * 1000;

/**
 * Extraction runs in the background now, so it needs the same two numbers, and
 * takes them from authoring rather than inventing a second cadence: one poll
 * rhythm across the run, and a run that has outlived the server's own execution
 * timeout is finished either way.
 */
export const EXTRACT_POLL_INTERVAL_MS = AUTHORING_POLL_INTERVAL_MS;
export const EXTRACT_STALL_MS = AUTHORING_STALL_MS;

export type BrandRunState =
  | { kind: 'extracting'; startedAt: number; progressUnavailable: boolean }
  | { kind: 'extract_ready' }
  | { kind: 'extract_failed' }
  | { kind: 'authoring'; serverPhase: string | null; startedAt: number; progressUnavailable: boolean }
  | { kind: 'ready' }
  | { kind: 'failed' }
  | { kind: 'sources_expired' }
  | { kind: 'stalled' };

/** Whether the run is still reading or writing, with nothing for the user to act on yet. */
export function isBrandRunInProgress(state: BrandRunState): boolean {
  switch (state.kind) {
    case 'extracting':
    case 'authoring':
      return true;
    case 'extract_ready':
    case 'extract_failed':
    case 'ready':
    case 'failed':
    case 'sources_expired':
    case 'stalled':
      return false;
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export type BrandRunStatusInput = {
  data: DsBrandDocumentGenerateStatusResponse | undefined;
  isError: boolean;
  errorStatus: number | null;
};

/** Draft phases the server's own terminal statuses map to. */
export type BrandRunOutcomePhase = 'document_review' | 'authoring_failed' | 'sources_expired';

/** Terminal draft phase a status response maps to, or `null` while still in progress. */
export function terminalPhaseFor(data: DsBrandDocumentGenerateStatusResponse): BrandRunOutcomePhase | null {
  if (data.status === 'completed') {
    return (data.document ?? '') === '' ? 'authoring_failed' : 'document_review';
  }
  if (data.status === 'failed') {
    return data.reason === 'no_readable_content' ? 'sources_expired' : 'authoring_failed';
  }
  return null;
}

/**
 * The draft phase a poll result should be PERSISTED as, or `null` while the run
 * is still legitimately in flight. Server outcomes plus the client's own stall
 * verdict: a 404 (the run is gone) or a run that has outlived the server's
 * execution timeout is never going to answer, so it stops being `authoring` —
 * which is what stops the background poll.
 */
export function phaseToPersistFor(
  status: BrandRunStatusInput,
  startedAt: number,
  now: number,
): BrandRunOutcomePhase | 'stalled' | null {
  if (status.data != null) {
    const terminal = terminalPhaseFor(status.data);
    if (terminal != null) return terminal;
  }
  if (status.isError && status.errorStatus === 404) return 'stalled';
  if (now - startedAt > AUTHORING_STALL_MS) return 'stalled';
  return null;
}

/** Draft phases an extract status response maps to. */
export type BrandExtractOutcomePhase = 'extract_ready' | 'extract_failed';

export type BrandExtractStatusInput = {
  data: DsGlobalStylesProcessStatusResponse | undefined;
  isError: boolean;
  errorStatus: number | null;
};

/**
 * The draft phase an extract poll should be PERSISTED as, or `null` while the
 * run is still legitimately in flight.
 *
 * A completed run with no styles is a failure, not a review: that is the same
 * verdict the old blocking modal reached ("We couldn't find any styles in your
 * sources"), and there is nothing to open a review on.
 *
 * Poll errors are read exactly as the authoring half reads them, and for the
 * same reason: at one request every 2.5s over a 25-minute window, a run that
 * ended on the first transient 5xx or dropped connection would be failing on
 * noise while the server is still working. Only a definitive 404 (the run is
 * gone) or outliving the server's own execution timeout ends it; anything else
 * stays `extracting`, where `progressUnavailable` says the progress is the part
 * that is missing, not the run.
 */
export function extractPhaseToPersistFor(
  status: BrandExtractStatusInput,
  startedAt: number,
  now: number,
): BrandExtractOutcomePhase | null {
  if (status.data != null) {
    if (status.data.status === 'completed') return status.data.styles == null ? 'extract_failed' : 'extract_ready';
    if (status.data.status === 'failed') return 'extract_failed';
  }
  if (status.isError && status.errorStatus === 404) return 'extract_failed';
  if (now - startedAt > EXTRACT_STALL_MS) return 'extract_failed';
  return null;
}

function fromPhase(phase: BrandWizardDraftPhase): BrandRunState | null {
  switch (phase) {
    case 'extract_ready':
      return { kind: 'extract_ready' };
    case 'extract_failed':
      return { kind: 'extract_failed' };
    case 'extracting':
      return null;
    case 'document_review':
      return { kind: 'ready' };
    case 'authoring_failed':
      return { kind: 'failed' };
    case 'sources_expired':
      return { kind: 'sources_expired' };
    case 'stalled':
      return { kind: 'stalled' };
    case 'authoring':
      return null;
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}

/** Styles tab badge text for a run state, or `undefined` when there is no live run. */
export function brandRunBadge(state: BrandRunState | null): string | undefined {
  if (state == null) return undefined;
  switch (state.kind) {
    case 'extracting':
      return 'Reading';
    case 'extract_ready':
      return 'Review';
    case 'extract_failed':
      return 'Attention';
    case 'authoring':
      return 'Writing';
    case 'ready':
      return 'Draft';
    case 'failed':
    case 'sources_expired':
    case 'stalled':
      return 'Attention';
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

/**
 * The run state of a draft in the extract half. Separate from
 * `deriveBrandRunState` because it reads a different endpoint's response;
 * the tracker picks by phase, and both feed the same `BrandRunState` union so
 * the banner, snackbar and badge stay one switch each.
 */
export function deriveBrandExtractState(
  draft: BrandWizardDraft,
  status: BrandExtractStatusInput,
  now: number,
): BrandRunState {
  const stored = fromPhase(draft.phase);
  if (stored != null) return stored;
  const persist = extractPhaseToPersistFor(status, draft.startedAt, now);
  if (persist != null) return fromPhase(persist) ?? { kind: 'extract_failed' };
  return { kind: 'extracting', startedAt: draft.startedAt, progressUnavailable: status.isError };
}

export function deriveBrandRunState(draft: BrandWizardDraft, status: BrandRunStatusInput, now: number): BrandRunState {
  const stored = fromPhase(draft.phase);
  if (stored != null) return stored;
  const persist = phaseToPersistFor(status, draft.startedAt, now);
  if (persist != null) return fromPhase(persist) ?? { kind: 'failed' };
  return {
    kind: 'authoring',
    serverPhase: status.data?.phase ?? null,
    startedAt: draft.startedAt,
    progressUnavailable: status.isError,
  };
}
